import React, { useState, useRef } from 'react';
import { Button, TextField, MenuItem, InputLabel, FormControl, Select, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, FormHelperText, Box, Typography } from '@mui/material';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress } from '@mui/material';

function FeedbackModal({ open, setOpen, userName, setSnackbarInfo }) {
    const [feedbackType, setFeedbackType] = useState('');
    const [description, setDescription] = useState('');
    const [screenshots, setScreenshots] = useState([]);
    const [feedbackTypeError, setFeedbackTypeError] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleFeedbackTypeChange = (event) => {
        setFeedbackType(event.target.value);
        setFeedbackTypeError(false);
    };

    const handleDescriptionChange = (event) => {
        setDescription(event.target.value);
        setDescriptionError(false);
    };

    const handleScreenshotChange = (event) => {
        const files = Array.from(event.target.files);
        files.forEach(file => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setScreenshots(prev => [...prev, { file, preview: reader.result }]);
                };
                reader.readAsDataURL(file);
            }
        });
        event.target.value = '';
    };

    const handleRemoveScreenshot = (index) => {
        setScreenshots(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        // Validations
        if (!feedbackType || !description) {
            if (!feedbackType) setFeedbackTypeError(true);
            if (!description) setDescriptionError(true);
            return;
        }

        setIsSubmitting(true);

        const url = `https://api.trello.com/1/cards?key=${process.env.REACT_APP_TRELLO_API_KEY}&token=${process.env.REACT_APP_TRELLO_TOKEN}`;
        const formData = new FormData();
        const listId = '6542b4b35231db341dc06b10';
        formData.append('idList', listId);
        formData.append('name', `Feedback: ${feedbackType} from ${userName}`);
        formData.append('desc', description);

        try {
            //creating card without attachements first
            const response = await axios.post(url, formData);
            console.log('Card created:', response.data);
            const cardId = response.data.id;
            console.log({ cardId, screenshots })

            // Now attach the screenshots=
            for (let i = 0; i < screenshots.length; i++) {
                const attachmentFormData = new FormData();
                attachmentFormData.append('file', screenshots[i].file);
                await axios.post(`https://api.trello.com/1/cards/${cardId}/attachments?key=${process.env.REACT_APP_TRELLO_API_KEY}&token=${process.env.REACT_APP_TRELLO_TOKEN}`, attachmentFormData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }
            setIsSubmitting(false);
            setSnackbarInfo({ open: true, severity: 'success', message: 'Feedback submitted successfully' });
            handleClose();
        } catch (error) {
            console.error('Error creating card:', error);
            setSnackbarInfo({ open: true, severity: 'error', message: 'Error submitting feedback' });
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                PaperProps={{ style: { width: '50%', height: '60%' } }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid lightgrey', display: 'flex', justifyContent: 'space-between', alignItems: 'center'  }}>
                    RWEdataLab Feedback Form
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                    </DialogTitle>
                <DialogContent sx={{ borderBottom: '1px solid lightgrey', padding: '14px' }}>
                    <FormControl fullWidth margin="normal" error={feedbackTypeError}>
                        <InputLabel>Type of Feedback</InputLabel>
                        <Select
                            value={feedbackType}
                            label="Type of Feedback"
                            onChange={handleFeedbackTypeChange}
                        >
                            <MenuItem value="Bug">Bug</MenuItem>
                            <MenuItem value="Enhancement">Enhancement</MenuItem>
                            <MenuItem value="General">General</MenuItem>
                        </Select>
                        {feedbackTypeError && <FormHelperText>This field is required.</FormHelperText>}
                    </FormControl>
                    <TextField
                        label="Generally describe what you were attempting to do"
                        multiline
                        rows={4}
                        value={description}
                        onChange={handleDescriptionChange}
                        fullWidth
                        margin="normal"
                        error={descriptionError}
                        helperText={descriptionError ? "This field is required." : ""}
                    />
                    <Typography variant="body2" color="textSecondary">Attach any relevant screenshots to help us understand your feedback better.</Typography>

                    <Button
                        variant="outlined"
                        component="label"
                        style={{ width: '40%', margin: '0 auto', }}
                    >
                        Upload Screenshot
                        <input
                            type="file"
                            hidden
                            multiple
                            onChange={handleScreenshotChange}
                            ref={fileInputRef}
                        />
                    </Button>
                    {screenshots.length > 0 && (
                    <Box sx={{ display: 'flex', overflowX: 'auto', marginTop: '10px', padding: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '4px' }}>
                        {screenshots.map((screenshot, index) => (
                            <Box key={index} sx={{ position: 'relative', marginRight: '10px' }}>
                                <img src={screenshot.preview} alt={`Screenshot ${index + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px', border: '1px solid lightgrey' }} />
                                <IconButton size="small" sx={{ position: 'absolute', right: 0, top: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)' }} onClick={() => handleRemoveScreenshot(index)}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={handleClose}
                        sx={{
                            backgroundColor: 'gray',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'gray',
                            },
                        }}
                    >
                        Close
                    </Button>
                    <Button variant="contained" onClick={() => handleSubmit()} disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default FeedbackModal;
